/**
 * Anchor tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects_ii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const anchorTattoosPack: ToilePack = {
    slug: 'anchor-tattoos',
    category: 'tattoo',
    name: 'Anchor tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'rope', label: 'Anchor and rope', subject: "a ship's anchor with a length of rope coiled loosely round its shank and stock"},
        {key: 'roses', label: 'Anchor and roses', subject: 'an anchor with two roses and leaves growing up around its curved arms'},
        {key: 'small', label: 'Small anchor', subject: 'a small simple anchor in a single fine line, the ring at the top and the two flukes curved'},
    ],
    styles: [
        {key: 'traditional', label: 'Traditional', style: 'American traditional tattoo flash, bold black outlines, heavy black shading and a limited flat palette', palette: 'red, yellow and green accents with black', framing: 'centered with a wide white margin'},
        {key: 'fineline', label: 'Fine line', style: 'a single-needle fine line tattoo drawing, one uniform hairline stroke, delicate detail and almost no shading', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin outline with all shading built from stippled dots and dot gradients', palette: 'black ink only', framing: 'centered with a wide white margin'},
    ],
    directions: ['a ship', 'red ink', 'wrist'],
    directionHint: 'try: a ship',
    seo: {
        title: 'Anchor tattoo designs: rope, roses, small fine line',
        metaDescription: 'Nine anchor tattoo designs: an anchor wrapped in rope, an anchor with roses and a small fine-line anchor, each in three styles.',
        h1: 'Anchor tattoo: anchor and rope, anchor and roses, small anchor',
        tagline: 'Three anchor designs, each drawn in traditional, fine line and dotwork.',
        intro: "Nine anchor tattoo designs on white paper: a ship's anchor with a length of rope coiled round it, an anchor with two roses growing up its arms, and a small anchor in one fine line. The anchor is the sailor's mark and a sign of staying steady, worn on the forearm, the wrist or the finger. Type a different anchor or a twist, such as a ship, and the same styles carry it.",
        useCases: [
            'a small anchor for the finger or wrist',
            'an anchor with roses for the forearm',
            'a rope anchor for a sailing theme',
            'a design sheet to show the artist',
        ],
        faq: [
            {q: 'What do the nine designs show?', a: 'Three anchor subjects drawn three ways: an anchor and rope, an anchor with roses and a small anchor in one line, in traditional, fine line and dotwork.'},
            {q: 'Can I ask for a different anchor or a twist?', a: 'Yes. The direction field takes any subject or twist, such as a ship; a typed direction is a request, so the result may differ from the samples.'},
            {q: 'What is the difference between the three styles?', a: 'Traditional has bold black outlines, heavy shading and a limited flat palette; fine line is one uniform hairline with almost no shading; dotwork is a thin outline with all shading built from stippled dots.'},
            {q: 'Are these photographs of tattoos on skin?', a: 'No. Each design is a line drawing on a plain white background, with no skin, arm or body in it.'},
        ],
    },
}
